/**
 * Combobox ricercabile sul catalogo CER (Elenco europeo dei rifiuti).
 *
 * - Ricerca per codice (accetta "160214", "16 02 14", parziali "16 02") e per
 *   testo della dicitura ufficiale.
 * - Input monospace con formattazione automatica XX XX XX quando si digitano
 *   cifre; per chi conosce il codice il flusso resta veloce: digita → prima
 *   voce evidenziata → Enter.
 * - Tastiera: frecce su/giù, Enter seleziona la voce evidenziata, ESC chiude
 *   la lista (con preventDefault → il Modal sopra non si chiude).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatCerInput, searchCatalog, type CerCatalogEntry,
} from '../../lib/cerCatalog';

interface Props {
  value: string;
  onChange: (value: string) => void;
  /** Selezione esplicita di una voce a catalogo (click o Enter). */
  onSelect: (entry: CerCatalogEntry) => void;
  placeholder?: string;
  testid?: string;
}

const inputCls =
  'w-full font-mono text-sm text-ink-dark border border-stone-border rounded px-3 py-2 ' +
  'focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

export default function CerCombobox({ value, onChange, onSelect, placeholder, testid }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => (open ? searchCatalog(value, 6) : []), [open, value]);

  // Chiudi la lista su click fuori dal combobox.
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  function select(entry: CerCatalogEntry) {
    onChange(entry.code);
    onSelect(entry);
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault(); // il Modal ignora gli ESC già "consumati"
        setOpen(false);
      }
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      setHighlight(0);
      e.preventDefault();
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const entry = results[highlight];
      if (entry) select(entry);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        className={inputCls}
        value={value}
        placeholder={placeholder ?? t('settings.cer_combobox.placeholder')}
        data-testid={testid}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        onChange={(e) => {
          onChange(formatCerInput(e.target.value));
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => { if (value.trim()) setOpen(true); }}
        onKeyDown={handleKeyDown}
      />
      {open && results.length > 0 && (
        <ul
          className="absolute z-10 mt-1 w-full bg-white border border-stone-border rounded-lg shadow-lg overflow-hidden"
          role="listbox"
          data-testid={testid ? `${testid}-list` : undefined}
        >
          {results.map((entry, i) => (
            <li key={entry.code} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                data-testid={testid ? `${testid}-option-${entry.code.replace(/ /g, '')}` : undefined}
                onMouseDown={(e) => e.preventDefault() /* non perdere il focus prima del click */}
                onClick={() => select(entry)}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full flex items-baseline gap-2 px-3 py-2 text-left text-sm ${
                  i === highlight ? 'bg-sage-light' : 'bg-white'
                }`}
              >
                <span className="font-mono font-medium text-ink-dark flex-shrink-0">
                  {entry.code}{entry.hazardous ? '*' : ''}
                </span>
                <span className="text-xs text-ink-soft truncate">{entry.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
